import { indexes } from "./common"

export type FilterFn<T> = (value: T) => boolean
export type MapFn<T, U> = (value: T) => U
export type FilterMapFn<T, U> = (value: T) => [true, U] | false | [false] | [false, ...unknown[]]
export type FlatMapFn<T, U> = (value: T) => Iterable<U>
export type ReduceFn<T> = (acc: T, value: T) => T
export type FoldFn<T, U> = (acc: U, value: T) => U
type Flatten<T> = T extends Iterable<infer Item> ? Item : T
type Defined<T> = T extends undefined ? never : T
type Some<T> = T extends null ? never : T

abstract class BaseLazyIterator<I, T> implements IterableIterator<T> {
    [Symbol.iterator](): this {
        return this
    }

    abstract next(): IteratorResult<T, undefined>

    skip(n: number): this {
        for (let i = 0; i < n; i++) this.next()
        return this
    }

    nth(n: number): IteratorResult<T, undefined> {
        for (let i = 1; i < n; i++) this.next()
        return this.next()
    }

    take(n: number): TakeIterator<T> {
        return new TakeIterator(this, n)
    }

    repeat(): RepeatIterator<I, T> {
        return new RepeatIterator(this)
    }

    map<U>(fn: MapFn<T, U>): MapIterator<T, U> {
        return new MapIterator(this, fn)
    }

    filter(fn: FilterFn<T>): FilterIterator<T> {
        return new FilterIterator(this, fn)
    }

    flat(): FlatIterator<T> {
        return new FlatIterator(this)
    }

    group(size: number): GroupIterator<T> {
        return new GroupIterator(this, size)
    }

    zip<U>(other: Iterable<U>): ZipIterator<T, U> {
        return new ZipIterator(this, other)
    }

    permute<U>(other: Iterable<U>): PermuteIterator<T, U> {
        return new PermuteIterator(this, other)
    }

    chain(other: Iterator<T>): ChainIterator<T> {
        return new ChainIterator(this, other)
    }

    filterMap<U>(fn: FilterMapFn<T, U>): FilterMapIterator<T, U> {
        return new FilterMapIterator(this, fn)
    }

    zipMap<U>(fn: MapFn<T, U>): ZipMapIterator<T, U> {
        return new ZipMapIterator(this, fn)
    }

    filterZipMap<U>(fn: FilterMapFn<T, U>): FilterZipMapIterator<T, U> {
        return new FilterZipMapIterator(this, fn)
    }

    flatMap<U>(fn: FlatMapFn<T, U>): FlatMapIterator<T, U> {
        return new FlatMapIterator(this, fn)
    }

    defined(): FilterIterator<Defined<T>> {
        return this.filter((v) => v !== undefined) as FilterIterator<Defined<T>>
    }

    some(): FilterIterator<Some<T>> {
        return this.filter((v) => v !== null) as FilterIterator<Some<T>>
    }

    enumerate(): ZipIterator<T, number> {
        return this.zip(indexes())
    }

    any(fn: FilterFn<T>): boolean {
        for (const item of this) {
            if (fn(item)) return true
        }

        return false
    }

    all(fn: FilterFn<T>): boolean {
        for (const item of this) {
            if (!fn(item)) return false
        }

        return true
    }

    first(fn: FilterFn<T>): [true, T] | [false] {
        for (const item of this) {
            if (fn(item)) return [true, item]
        }

        return [false]
    }

    position(fn: FilterFn<T>): number | null {
        let i = 0

        for (const item of this) {
            if (fn(item)) return i
            i++
        }

        return null
    }

    collect(): T[] {
        const out: T[] = []

        for (const item of this) {
            out.push(item)
        }

        return out
    }

    count(fn: FilterFn<T>): number {
        let count = 0

        for (const item of this) {
            if (fn(item)) count++
        }

        return count
    }

    reduce(fn: ReduceFn<T>): [true, T] | [false] {
        const next = this.next()
        if (next.done) return [false]
        let acc = next.value

        for (const item of this) {
            acc = fn(acc, item)
        }

        return [true, acc]
    }

    fold<U>(fn: FoldFn<T, U>, init: U): U {
        let acc = init

        for (const item of this) {
            acc = fn(acc, item)
        }

        return acc
    }
}

export class LazyIterator<T> extends BaseLazyIterator<T, T> {
    private internal

    constructor(iterable: Iterable<T>) {
        super()
        this.internal = iterable[Symbol.iterator]()
    }

    static from<Obj extends {}>(obj: Obj): LazyIterator<[string, Obj[keyof Obj]]> {
        return new LazyIterator(Object.entries(obj))
    }

    next(): IteratorResult<T, undefined> {
        return this.internal.next()
    }
}

class TakeIterator<T> extends BaseLazyIterator<T, T> {
    constructor(
        private internal: Iterator<T>,
        private n: number,
    ) {
        super()
    }

    next(): IteratorResult<T, undefined> {
        if (this.n--) return this.internal.next()
        return { done: true, value: undefined }
    }
}

class RepeatIterator<I, T> extends BaseLazyIterator<I, T> {
    private loop: T[] = []
    private i = 0

    constructor(private internal: BaseLazyIterator<I, T> | null) {
        super()
    }

    next(): IteratorResult<T, undefined> {
        if (this.internal) {
            const next = this.internal.next()

            if (next.done) this.internal = null
            else {
                this.loop.push(next.value)
                return next
            }
        }

        if (this.loop.length === 0) return { done: true, value: undefined }
        if (this.i === this.loop.length) this.i = 0
        const i = this.i++
        return { done: false, value: this.loop[i] }
    }
}

class MapIterator<T, U> extends BaseLazyIterator<T, U> {
    constructor(
        private internal: Iterator<T>,
        private fn: MapFn<T, U>,
    ) {
        super()
    }

    next(): IteratorResult<U, undefined> {
        const next = this.internal.next()
        if (next.done) return next
        return { done: false, value: this.fn(next.value) }
    }
}

class FilterIterator<T> extends BaseLazyIterator<T, T> {
    constructor(
        private internal: Iterator<T>,
        private fn: FilterFn<T>,
    ) {
        super()
    }

    next(): IteratorResult<T, undefined> {
        const next = this.internal.next()
        if (next.done) return next
        if (this.fn(next.value)) return next
        return this.next()
    }
}

class FlatIterator<T> extends BaseLazyIterator<T, Flatten<T>> {
    private buf: Iterator<T> = [][Symbol.iterator]()

    constructor(private internal: Iterator<T>) {
        super()
    }

    next(): IteratorResult<Flatten<T>, undefined> {
        let bufNext = this.buf.next()

        while (bufNext.done) {
            const next = this.internal.next()
            if (next.done) return next

            if (next.value && typeof next.value === "object" && Symbol.iterator in next.value) {
                this.buf = (next.value as Iterable<T>)[Symbol.iterator]()
            } else return next as IteratorResult<Flatten<T>, undefined>

            bufNext = this.buf.next()
        }

        return bufNext as IteratorResult<Flatten<T>, undefined>
    }
}

class GroupIterator<T> extends BaseLazyIterator<T, T[]> {
    constructor(
        private internal: Iterator<T>,
        private size: number,
    ) {
        super()
    }

    next(): IteratorResult<T[], undefined> {
        const buf = []

        while (true) {
            const next = this.internal.next()

            if (next.done) {
                if (buf.length > 0) {
                    return { done: false, value: buf }
                }
                
                return next
            }

            buf.push(next.value)

            if (buf.length === this.size) {
                return { done: false, value: buf }
            }
        }
    }
}

class ZipIterator<T, U> extends BaseLazyIterator<T, [T, U]> {
    private other: Iterator<U>

    constructor(
        private internal: Iterator<T>,
        other: Iterable<U>,
    ) {
        super()
        this.other = other[Symbol.iterator]()
    }

    next(): IteratorResult<[T, U], undefined> {
        const internalNext = this.internal.next()
        if (internalNext.done) return internalNext
        const otherNext = this.other.next()
        if (otherNext.done) return otherNext
        return { done: false, value: [internalNext.value, otherNext.value] }
    }
}

class PermuteIterator<T, U> extends BaseLazyIterator<T, [T, U]> {
    private other: Iterator<U>
    private otherEnded = false
    private current: T | null = null
    private buf: U[] = []
    private bufIdx = 0

    constructor(
        private internal: Iterator<T>,
        other: Iterable<U>,
    ) {
        super()
        this.other = other[Symbol.iterator]()
    }

    next(): IteratorResult<[T, U], undefined> {
        if (!this.current) {
            const next = this.internal.next()
            if (next.done) return next
            this.current = next.value
            this.bufIdx = 0
        }

        if (!this.otherEnded) {
            const next = this.other.next()

            if (!next.done) {
                this.buf.push(next.value)
                return { done: false, value: [this.current, next.value] }
            }

            this.otherEnded = true
            this.current = null
            return this.next()
        }

        if (this.bufIdx >= this.buf.length) {
            this.current = null
            return this.next()
        }

        return { done: false, value: [this.current, this.buf[this.bufIdx++]] }
    }
}

class ChainIterator<T> extends BaseLazyIterator<T, T> {
    constructor(
        private internal: Iterator<T> | null,
        private other: Iterator<T>,
    ) {
        super()
    }

    next(): IteratorResult<T, undefined> {
        if (!this.internal) return this.other.next()
        const next = this.internal.next()

        if (next.done) {
            this.internal = null
            return this.other.next()
        }

        return next
    }
}

class FilterMapIterator<T, U> extends BaseLazyIterator<T, U> {
    constructor(
        private internal: Iterator<T>,
        private fn: FilterMapFn<T, U>,
    ) {
        super()
    }

    next(): IteratorResult<U, undefined> {
        const next = this.internal.next()
        if (next.done) return next
        const tryMapped = this.fn(next.value)
        if (tryMapped === false) return this.next()
        const [filter, mapped] = tryMapped
        if (filter) return { done: false, value: mapped }
        return this.next()
    }
}

class ZipMapIterator<T, U> extends BaseLazyIterator<T, [T, U]> {
    constructor(
        private internal: Iterator<T>,
        private fn: MapFn<T, U>,
    ) {
        super()
    }

    next(): IteratorResult<[T, U], undefined> {
        const next = this.internal.next()
        if (next.done) return next
        return { done: false, value: [next.value, this.fn(next.value)] }
    }
}

class FilterZipMapIterator<T, U> extends BaseLazyIterator<T, [T, U]> {
    constructor(
        private internal: Iterator<T>,
        private fn: FilterMapFn<T, U>,
    ) {
        super()
    }

    next(): IteratorResult<[T, U], undefined> {
        const next = this.internal.next()
        if (next.done) return next
        const tryMapped = this.fn(next.value)
        if (tryMapped === false) return this.next()
        const [filter, mapped] = tryMapped
        if (filter) return { done: false, value: [next.value, mapped] }
        return this.next()
    }
}

class FlatMapIterator<T, U> extends BaseLazyIterator<T, U> {
    private buf: Iterator<U> = [][Symbol.iterator]()

    constructor(
        private internal: Iterator<T>,
        private fn: FlatMapFn<T, U>,
    ) {
        super()
    }

    next(): IteratorResult<U, undefined> {
        let bufNext = this.buf.next()

        while (bufNext.done) {
            const next = this.internal.next()
            if (next.done) return next
            this.buf = this.fn(next.value)[Symbol.iterator]()
            bufNext = this.buf.next()
        }

        return bufNext
    }
}
